import "./Invoice.css";
import "./Invoicefiles/Invoiceadditions.css";
import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaWallet, FaEdit, FaHistory, FaTimes, FaCheck, FaFilePdf, FaCoins } from "react-icons/fa";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { db } from "../../firebase/firebase";
import { collection, getDocs, doc, updateDoc, runTransaction } from "firebase/firestore";
import {
  useClientsStatusMap,
  resolveClientStatus,
  getProjectRef,
  PROJECT_REF_BADGE_STYLE,
  resolveProjectNumber,
  formatProjectSeq,
} from "../../hooks/useClientsStatusMap";
import { useToast } from "../../components/Dashboard/Invoicefiles/Usetoast";
import ToastContainer from "../../components/Dashboard/Invoicefiles/Toastcontainer";
import usePagination from "../../hooks/usePagination";
import Pagination from "./Pagination";

// ---------------------------------------------------------------------
// PDF branding — identical to the theme used on Invoice.js / Materials.js
// / Estimation1.js, so every generated document across the app looks
// consistent.
// ---------------------------------------------------------------------
const COMPANY_NAME = "Sarrathi Constructions";
const COMPANY_ADDRESS =
  "82, Jagannathan Salai, Anjugam Nagar,SRP Colony, Kolathur, Chennai - 600082.";
const LOGO_PATH = "/logo/company logo.jpeg";
const COMPANY_EMAIL = "build@sarrathiconstructions.com";
const COMPANY_PHONE = "98405 24873, 98407 98059";
const COMPANY_WEBSITE = "www.sarrathiconstructions.com";

const PDF_THEME = {
  colors: {
    primaryDark: [23, 32, 51],
    primary: [22, 37, 61],
    accentGold: [199, 156, 68],
    textMuted: [110, 118, 132],
    textBody: [55, 65, 81],
    borderLight: [225, 228, 233],
    rowStripe: [246, 247, 250],
    balanceRed: [196, 78, 66],
    paidGreen: [46, 138, 89],
  },
  fontFamily: "helvetica",
};

const loadCompanyLogoDataUrl = async () => {
  try {
    const res = await fetch(LOGO_PATH);
    if (!res.ok) throw new Error("Logo not found");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error("Failed to load company logo for PDF:", err);
    return null;
  }
};

const sanitizeFileName = (name) => {
  const cleaned = (name || "").trim().replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return cleaned || "Client";
};

// jsPDF's built-in "helvetica" font has no glyph for "₹" — use the
// ASCII-safe "Rs." prefix on every PDF, exactly like the rest of the app.
const formatRupeesPdf = (value) => {
  const num = Number(value);
  if (!value || Number.isNaN(num)) return "Rs. 0";
  return `Rs. ${num.toLocaleString("en-IN")}`;
};

const drawSectionTitle = (docPdf, text, x, y) => {
  const { colors } = PDF_THEME;
  docPdf.setFont(PDF_THEME.fontFamily, "bold");
  docPdf.setFontSize(11.5);
  docPdf.setTextColor(...colors.primaryDark);
  docPdf.text(text.toUpperCase(), x, y);
  docPdf.setDrawColor(...colors.accentGold);
  docPdf.setLineWidth(1.1);
  docPdf.line(x, y + 5, x + 34, y + 5);
  return y + 22;
};

const baseTableOptions = () => ({
  theme: "grid",
  styles: {
    font: PDF_THEME.fontFamily,
    fontSize: 9,
    cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
    textColor: PDF_THEME.colors.textBody,
    lineColor: PDF_THEME.colors.borderLight,
    lineWidth: 0.6,
    valign: "middle",
  },
  headStyles: {
    fillColor: PDF_THEME.colors.primary,
    textColor: 255,
    fontStyle: "bold",
    fontSize: 9,
    halign: "left",
  },
  alternateRowStyles: { fillColor: PDF_THEME.colors.rowStripe },
});

// Height reserved at the top of every page for the repeating slim header
// on pages 2+ — table content set with margin.top = PDF_HEADER_BAND never
// starts underneath it.
const PDF_HEADER_BAND = 74;

// ===========================================================================
// PAYMENTS PAGE — Materials/Payments split.
//
// This page owns everything about a project's MONEY: Original Estimation,
// Final Quotation (editable here), the full Payment History / Payment
// Timeline, and the running Balance. It reads the exact same `invoices`
// Firestore collection Invoice.js already writes to — nothing about the
// payment data model changes and nothing is duplicated.
//
// IMPORTANT — Invoice.js remains the single source for RECORDING a
// payment (its "Add Payment" flow). This page never records a new
// payment through a different schema; whenever the admin clicks "Add
// Payment" on Invoice.js, that payment automatically shows up here too,
// because both pages simply read the same `invoices/{id}.payments` array.
//
// The one thing this page CAN do that Invoice.js can't is edit the Final
// Quotation after the fact — see openEditQuotation/saveQuotation below.
// Editing it only ever touches `finalEstimation` + recalculates
// `balanceAmount`; every payment already recorded is left completely
// untouched.
//
// BALANCE CONSISTENCY: every payment entry's `balanceAfterPayment` field
// is a SNAPSHOT taken at the moment that payment was recorded. If the
// Final Quotation is edited afterward via saveQuotation below, that
// stored snapshot goes stale (it keeps reflecting the OLD Final
// Quotation). getCorrectedBalanceAfterPayment recomputes the balance
// live against the invoice's CURRENT finalEstimation, so this page's
// Manage Payments modal and its PDF always agree with Invoice.js's View
// Invoice modal / Payment History modal — mirroring the same fix applied
// there.
// ---------------------------------------------------------------------

const getEntrySequence = (entry) => {
  if (!entry) return null;
  if (entry.invoiceSequence !== undefined && entry.invoiceSequence !== null) {
    return Number(entry.invoiceSequence);
  }
  if (typeof entry.invoiceNumber === "number") return entry.invoiceNumber;
  return null;
};

// ---------------------------------------------------------------------
// Same invoice-numbering scheme Invoice.js uses (see its "Invoice
// numbering v2" doc-block) — duplicated here only because Invoice.js's
// helpers aren't exported, so Make Payment below can generate the exact
// same mainInvoiceNumber / paymentInvoiceNumber format and stay
// byte-for-byte consistent with payments recorded from Invoice.js.
// ---------------------------------------------------------------------
const MAIN_INVOICE_PREFIX = "SC-INV";

// Formats the final sequence portion of a main invoice number as 3
// digits, left-padded with zeros (1 -> "001", 25 -> "025"), without ever
// truncating a value that already has more than 3 digits (1001 -> "1001").
const formatInvoiceSeq = (seq) => {
  const num = Number(seq);
  if (Number.isNaN(num)) return String(seq).padStart(3, "0");
  return String(num).padStart(3, "0");
};

// Pulls the trailing numeric sequence out of a client ID (e.g. "CL-003"
// -> "003") so the main invoice number can visually align with the
// client's own ID. Returns null when unusable, so callers fall back to
// the existing counter-based sequence instead of producing a broken
// number.
const extractClientNumericSeq = (clientId) => {
  if (!clientId || typeof clientId !== "string") return null;
  const match = clientId.trim().match(/(\d+)\s*$/);
  if (!match) return null;
  return formatInvoiceSeq(match[1]);
};

const buildMainInvoiceNumber = (year, clientSeq, projectSeq) =>
  `${MAIN_INVOICE_PREFIX}-${year}-${clientSeq}-P${projectSeq}`;

const buildPaymentInvoiceNumber = (mainInvoiceNumber, sequence) =>
  mainInvoiceNumber && sequence
    ? `${mainInvoiceNumber}-${String(sequence).padStart(2, "0")}`
    : null;

const formatInvoiceNumberDisplay = (entry, invoice) => {
  if (!entry) return "-";
  const seq = getEntrySequence(entry);

  if (invoice?.mainInvoiceNumber && seq) {
    return `${invoice.mainInvoiceNumber}-${String(seq).padStart(2, "0")}`;
  }
  if (typeof entry.paymentInvoiceNumber === "string" && entry.paymentInvoiceNumber.trim()) {
    return entry.paymentInvoiceNumber;
  }
  if (typeof entry.invoiceNumber === "string" && entry.invoiceNumber.trim()) {
    return entry.invoiceNumber;
  }
  return seq ? `Legacy #${seq}` : "-";
};

const formatMainInvoiceNumber = (invoice) => {
  if (!invoice) return "-";
  if (typeof invoice.mainInvoiceNumber === "string" && invoice.mainInvoiceNumber.trim()) {
    return invoice.mainInvoiceNumber;
  }
  return "-";
};

// Corrected, always-current balance-after-payment — mirrors the identical
// helper in Invoice.js. See the file-level comment above for why this is
// needed: `entry.balanceAfterPayment` is a stale snapshot once the Final
// Quotation has been edited via saveQuotation below.
const getCorrectedBalanceAfterPayment = (invoice, runningTotalPaid) => {
  const currentFinal = Number(invoice?.finalEstimation) || 0;
  return currentFinal - runningTotalPaid;
};

function Payments() {
  const { toasts, toast } = useToast();
  const { clientsById } = useClientsStatusMap();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, "invoices"));
      const list = [];
      snapshot.forEach((docSnap) => list.push({ id: docSnap.id, ...docSnap.data() }));

      list.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return bTime - aTime;
      });

      setInvoices(list);
    } catch (err) {
      console.error("Failed to load invoices:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const formatRupees = (value) => {
    const num = Number(value);
    if (!value || Number.isNaN(num)) return "₹ 0";
    return `₹ ${num.toLocaleString("en-IN")}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    try {
      const dateObj = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return dateObj.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const statusFor = useCallback(
    (invoice) => resolveClientStatus(clientsById, invoice.clientDocId, "In Progress"),
    [clientsById],
  );

  const statusPillClass = (status) => {
    if (status === "Completed") return "status-completed";
    if (status === "On Hold") return "status-onhold";
    return "status-progress";
  };

  const visibleInvoices = useMemo(
    () =>
      invoices.filter((inv) => {
        const status = statusFor(inv);
        return status !== "In Talk" && status !== "Rejected";
      }),
    [invoices, statusFor],
  );

  const filteredInvoices = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return visibleInvoices;

    return visibleInvoices.filter((inv) => {
      const clientId = (inv.clientId || "").toLowerCase();
      const name = (inv.clientName || "").toLowerCase();
      const email = (inv.email || "").toLowerCase();
      const mainNumber = (inv.mainInvoiceNumber || "").toLowerCase();

      return (
        clientId.includes(term) || name.includes(term) || email.includes(term) || mainNumber.includes(term)
      );
    });
  }, [search, visibleInvoices]);

  const {
    pageData: paginatedInvoices,
    ...invoicesPagination
  } = usePagination(filteredInvoices, { resetKey: search });

  const totalCollected = useMemo(
    () => visibleInvoices.reduce((sum, inv) => sum + (Number(inv.amountPaid) || 0), 0),
    [visibleInvoices],
  );
  const totalOutstanding = useMemo(
    () => visibleInvoices.reduce((sum, inv) => sum + (Number(inv.balanceAmount) || 0), 0),
    [visibleInvoices],
  );

  const sortedPaymentsFor = (invoice) =>
    [...(invoice.payments || [])].sort(
      (a, b) => (getEntrySequence(a) || 0) - (getEntrySequence(b) || 0),
    );

  // A project remains payable for as long as it has an outstanding
  // balance — same rule Invoice.js uses for its Add Payment icon.
  const isPayable = (invoice) => Number(invoice.balanceAmount) > 0;

  // =====================================================================
  // Make Payment — a separate action from Manage Payments (per the
  // Payments/Materials split). This reuses the exact same Add Payment
  // behavior, validation, invoice-numbering, and Firestore transaction
  // Invoice.js already implements, writing to the SAME `invoices/{id}`
  // document and the SAME `payments` array — no new collection, no
  // second payment schema. Invoice.js itself is untouched; this is a
  // second, independent entry point into the same data.
  // =====================================================================
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [paymentSaving, setPaymentSaving] = useState(false);

  const openAddPayment = (invoice) => {
    if (!isPayable(invoice)) return;
    setPayingInvoice(invoice);
    setPaymentAmount("");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentError("");
    setShowAddPaymentModal(true);
  };

  const closeAddPayment = () => {
    if (paymentSaving) return;
    setShowAddPaymentModal(false);
    setPayingInvoice(null);
    setPaymentAmount("");
    setPaymentDate("");
    setPaymentError("");
  };

  const handlePaymentAmountChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setPaymentAmount(value);
      if (paymentError) setPaymentError("");
    }
  };

  const paymentPreviewBalance = useMemo(() => {
    if (!payingInvoice) return 0;
    const currentBalance = Number(payingInvoice.balanceAmount) || 0;
    const amt = Number(paymentAmount) || 0;
    const preview = currentBalance - amt;
    return preview > 0 ? preview : 0;
  }, [payingInvoice, paymentAmount]);

  const saveAddPayment = async () => {
    if (!payingInvoice) return;

    const amt = Number(paymentAmount);

    if (!paymentAmount.trim() || Number.isNaN(amt) || amt <= 0) {
      setPaymentError("Enter a valid payment amount.");
      return;
    }

    if (!paymentDate) {
      setPaymentError("Select a payment date.");
      return;
    }

    const currentBalance = Number(payingInvoice.balanceAmount) || 0;

    if (amt > currentBalance) {
      setPaymentError("Payment amount cannot exceed the current balance.");
      return;
    }

    setPaymentSaving(true);
    const invoiceRef = doc(db, "invoices", payingInvoice.id);
    const mainCounterRef = doc(db, "counters", "mainInvoiceNumber");
    const clientRef = payingInvoice.clientDocId ? doc(db, "clients", payingInvoice.clientDocId) : null;
    const fallbackProjectNumber = payingInvoice.clientDocId
      ? resolveProjectNumber(clientsById, payingInvoice.clientDocId)
      : 1;

    try {
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(invoiceRef);

        if (!snap.exists()) {
          throw new Error("This invoice no longer exists.");
        }

        const data = snap.data();
        const priorBalance = Number(data.balanceAmount) || 0;
        const priorPaid = Number(data.amountPaid) || 0;
        const priorPayments = Array.isArray(data.payments) ? data.payments : [];

        if (amt > priorBalance) {
          throw new Error("Payment amount cannot exceed the current balance.");
        }

        // Same lazy, one-time mainInvoiceNumber generation Invoice.js's own
        // transaction performs — every child payment number is derived
        // from it, so it must exist before the very first payment.
        // Prefers the client's own numeric ID sequence, falling back to
        // the shared counter only when the client ID has no usable
        // numeric sequence.
        let mainInvoiceNumber = data.mainInvoiceNumber;
        let newCounterSeq = null;
        let generatedNewMainNumber = false;

        if (!mainInvoiceNumber) {
          const clientSnap = clientRef ? await transaction.get(clientRef) : null;
          const storedProjectNumber = clientSnap?.exists() ? clientSnap.data().projectNumber : null;
          const projectSeq = formatProjectSeq(storedProjectNumber || fallbackProjectNumber);

          const clientSeq = extractClientNumericSeq(data.clientId || payingInvoice.clientId);

          if (clientSeq !== null) {
            mainInvoiceNumber = buildMainInvoiceNumber(new Date().getFullYear(), clientSeq, projectSeq);
          } else {
            const counterSnap = await transaction.get(mainCounterRef);
            const lastSeq = counterSnap.exists()
              ? Number(counterSnap.data().lastSequence) || 0
              : 0;
            newCounterSeq = lastSeq + 1;
            mainInvoiceNumber = buildMainInvoiceNumber(
              new Date().getFullYear(),
              formatInvoiceSeq(newCounterSeq),
              projectSeq,
            );
          }
          generatedNewMainNumber = true;
        }

        const newBalance = priorBalance - amt;
        const nextSequence = priorPayments.length + 1;
        const paymentInvoiceNumber = buildPaymentInvoiceNumber(mainInvoiceNumber, nextSequence);

        const newInvoiceEntry = {
          invoiceSequence: nextSequence,
          paymentInvoiceNumber,
          paymentAmount: amt,
          paymentDate,
          paymentNotes: "",
          balanceAfterPayment: newBalance,
          createdAt: new Date().toISOString(),
        };

        transaction.update(invoiceRef, {
          ...(generatedNewMainNumber ? { mainInvoiceNumber } : {}),
          amountPaid: priorPaid + amt,
          balanceAmount: newBalance,
          payments: [...priorPayments, newInvoiceEntry],
          paymentCount: nextSequence,
          latestPaymentDate: paymentDate,
          latestInvoiceNumber: paymentInvoiceNumber,
        });

        if (newCounterSeq) {
          transaction.set(
            mainCounterRef,
            { lastSequence: newCounterSeq, updatedAt: new Date().toISOString() },
            { merge: true },
          );
        }
      });

      toast.success("Payment Recorded — Invoice Generated Successfully");
      closeAddPayment();
      await fetchInvoices();
    } catch (err) {
      console.error(err);
      const message = err.message || "Failed to record payment.";
      setPaymentError(message);
      toast.error(message);
    } finally {
      setPaymentSaving(false);
    }
  };

  // ---------------- Manage Payments modal ----------------
  const [showManageModal, setShowManageModal] = useState(false);
  const [managingInvoice, setManagingInvoice] = useState(null);

  const openManagePayments = (invoice) => {
    setManagingInvoice(invoice);
    setShowManageModal(true);
    setEditingQuotation(false);
    setQuotationError("");
  };

  const closeManagePayments = () => {
    if (quotationSaving) return;
    setShowManageModal(false);
    setManagingInvoice(null);
    setEditingQuotation(false);
    setQuotationError("");
  };

  const managingPayments = useMemo(
    () => (managingInvoice ? sortedPaymentsFor(managingInvoice) : []),
    [managingInvoice],
  );

  // Each card shows a running total (sum of every payment up to and
  // including itself), and a CORRECTED remaining balance recomputed live
  // against the invoice's current Final Quotation — see
  // getCorrectedBalanceAfterPayment — rather than each entry's stale
  // stored `balanceAfterPayment` snapshot.
  const managingPaymentsWithRunning = useMemo(() => {
    if (!managingInvoice) return [];
    let running = 0;
    return managingPayments.map((entry) => {
      running += Number(entry.paymentAmount) || 0;
      return {
        entry,
        runningTotal: running,
        correctedBalance: getCorrectedBalanceAfterPayment(managingInvoice, running),
      };
    });
  }, [managingPayments, managingInvoice]);

  // ---------------- Editable Final Quotation ----------------
  // Updating this only ever touches `finalEstimation` + recalculates
  // `balanceAmount` on the SAME invoice doc. Every existing payment in
  // `payments` is preserved exactly as-is — nothing is deleted or
  // recreated. Since `managingPaymentsWithRunning` above recomputes each
  // payment's displayed balance live from `managingInvoice.finalEstimation`,
  // updating that field here automatically keeps every card in Manage
  // Payments (and the PDF below) in sync — no per-entry rewrite needed.
  const [editingQuotation, setEditingQuotation] = useState(false);
  const [quotationInput, setQuotationInput] = useState("");
  const [quotationError, setQuotationError] = useState("");
  const [quotationSaving, setQuotationSaving] = useState(false);

  const openEditQuotation = () => {
    if (!managingInvoice) return;
    setQuotationInput(String(managingInvoice.finalEstimation ?? ""));
    setQuotationError("");
    setEditingQuotation(true);
  };

  const cancelEditQuotation = () => {
    if (quotationSaving) return;
    setEditingQuotation(false);
    setQuotationError("");
  };

  const handleQuotationInputChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setQuotationInput(value);
      if (quotationError) setQuotationError("");
    }
  };

  const saveQuotation = async () => {
    if (!managingInvoice) return;

    const newFinal = Number(quotationInput);
    const amountPaid = Number(managingInvoice.amountPaid) || 0;

    if (!quotationInput.trim() || Number.isNaN(newFinal) || newFinal <= 0) {
      setQuotationError("Enter a valid quotation amount.");
      return;
    }
    if (newFinal < amountPaid) {
      setQuotationError(
        `Final Quotation cannot be less than the amount already paid (${formatRupees(amountPaid)}).`,
      );
      return;
    }

    setQuotationSaving(true);
    try {
      const newBalance = newFinal - amountPaid;

      await updateDoc(doc(db, "invoices", managingInvoice.id), {
        finalEstimation: newFinal,
        balanceAmount: newBalance,
      });

      // Keep the linked Estimation record's displayed amount synchronized
      // with the Final Quotation — the Estimation page must never show a
      // stale value after the quotation is edited here.
      if (managingInvoice.estimationId) {
        await updateDoc(doc(db, "estimations", managingInvoice.estimationId), {
          estimationAmount: newFinal,
        });
      }

      const updated = { ...managingInvoice, finalEstimation: newFinal, balanceAmount: newBalance };
      setManagingInvoice(updated);
      setInvoices((prev) => prev.map((inv) => (inv.id === updated.id ? updated : inv)));

      toast.success("Final Quotation Updated Successfully");
      setEditingQuotation(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update Final Quotation.");
    } finally {
      setQuotationSaving(false);
    }
  };

  // =====================================================================
  // Generate PDF — Client Payment History report. Strictly read-only:
  // built entirely from `managingInvoice` + `managingPaymentsWithRunning`,
  // the exact same data already driving this modal on screen — including
  // the corrected, live-recomputed balance per payment.
  // =====================================================================
  const [paymentsPdfGenerating, setPaymentsPdfGenerating] = useState(false);

  const generatePaymentsPDF = async () => {
    if (!managingInvoice) return;

    setPaymentsPdfGenerating(true);
    try {
      const { colors } = PDF_THEME;
      const docPdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = docPdf.internal.pageSize.getWidth();
      const marginX = 40;

      // ---- Header — logo, company name, address ----
      const logoDataUrl = await loadCompanyLogoDataUrl();
      const headerTop = 40;
      const logoSize = 54;

      if (logoDataUrl) {
        try {
          docPdf.addImage(logoDataUrl, "JPEG", marginX, headerTop, logoSize, logoSize);
        } catch (imgErr) {
          console.error("Failed to draw logo on payments report PDF:", imgErr);
        }
      }

      const textX = marginX + logoSize + 16;
      const nameY = headerTop + logoSize / 2 - 6;

      docPdf.setFont(PDF_THEME.fontFamily, "bold");
      docPdf.setFontSize(17);
      docPdf.setTextColor(...colors.primaryDark);
      docPdf.text(COMPANY_NAME, textX, nameY);

      docPdf.setFont(PDF_THEME.fontFamily, "normal");
      docPdf.setFontSize(9);
      docPdf.setTextColor(...colors.textMuted);
      docPdf.text(COMPANY_ADDRESS, textX, nameY + 15, {
        maxWidth: pageWidth - marginX * 2 - logoSize - 16,
      });
      docPdf.text(`Phone: ${COMPANY_PHONE}`, textX, nameY + 27);
      docPdf.text(`Email: ${COMPANY_EMAIL}`, textX, nameY + 39);
      docPdf.text(`Website: ${COMPANY_WEBSITE}`, textX, nameY + 51);

            const logoRowBottom = Math.max(headerTop + logoSize, nameY + 51) + 16;
      docPdf.setDrawColor(...colors.accentGold);
      docPdf.setLineWidth(1.3);
      docPdf.line(marginX, logoRowBottom, pageWidth - marginX, logoRowBottom);

      const titleY = logoRowBottom + 26;

      docPdf.setFont(PDF_THEME.fontFamily, "bold");
      docPdf.setFontSize(15);
      docPdf.setTextColor(...colors.primaryDark);
      docPdf.text("PAYMENT HISTORY REPORT", marginX, titleY);

      docPdf.setFont(PDF_THEME.fontFamily, "normal");
      docPdf.setFontSize(9.5);
      docPdf.setTextColor(...colors.textMuted);
      docPdf.text(
        `Generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`,
        pageWidth - marginX,
        titleY,
        { align: "right" },
      );

      const headerBottom = titleY + 16;
      docPdf.setDrawColor(...colors.borderLight);
      docPdf.setLineWidth(0.8);
      docPdf.line(marginX, headerBottom, pageWidth - marginX, headerBottom);

      let cursorY = headerBottom + 22;

      // ---- Client Information ----
      cursorY = drawSectionTitle(docPdf, "Client Information", marginX, cursorY);

      autoTable(docPdf, {
        ...baseTableOptions(),
        startY: cursorY,
        showHead: false,
        styles: { ...baseTableOptions().styles, fontSize: 9.5 },
        body: [
          ["Client Name", managingInvoice.clientName || "-", "Client ID", managingInvoice.clientId || "-"],
          [
            "Project Type",
            managingInvoice.projectType || "-",
            "Main Invoice Number",
            formatMainInvoiceNumber(managingInvoice),
          ],
        ],
        columnStyles: {
          0: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 100 },
          1: { cellWidth: 185 },
          2: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 100 },
          3: { cellWidth: "auto" },
        },
        margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
      });

      cursorY = docPdf.lastAutoTable.finalY + 26;

      // ---- Quotation Summary ----
      cursorY = drawSectionTitle(docPdf, "Quotation Summary", marginX, cursorY);

      autoTable(docPdf, {
        ...baseTableOptions(),
        startY: cursorY,
        styles: { ...baseTableOptions().styles, halign: "center" },
        head: [["Final Quotation", "Amount Paid", "Balance"]],
        body: [[
          formatRupeesPdf(managingInvoice.finalEstimation),
          { content: formatRupeesPdf(managingInvoice.amountPaid), styles: { textColor: colors.paidGreen, fontStyle: "bold" } },
          { content: formatRupeesPdf(managingInvoice.balanceAmount), styles: { textColor: colors.balanceRed, fontStyle: "bold" } },
        ]],
        columnStyles: {
          0: { cellWidth: 172 },
          1: { cellWidth: 172 },
          2: { cellWidth: "auto" },
        },
        margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
      });

      cursorY = docPdf.lastAutoTable.finalY + 26;

      // ---- Payment History ----
      cursorY = drawSectionTitle(docPdf, "Payment History", marginX, cursorY);

      const historyRows = managingPaymentsWithRunning.map(({ entry, runningTotal, correctedBalance }) => [
        formatInvoiceNumberDisplay(entry, managingInvoice),
        formatDate(entry.paymentDate),
        { content: formatRupeesPdf(entry.paymentAmount), styles: { halign: "right" } },
        { content: formatRupeesPdf(runningTotal), styles: { halign: "right" } },
        { content: formatRupeesPdf(correctedBalance), styles: { halign: "right", textColor: colors.balanceRed } },
        entry.paymentNotes || "-",
      ]);

      if (historyRows.length === 0) {
        docPdf.setFont(PDF_THEME.fontFamily, "italic");
        docPdf.setFontSize(9.5);
        docPdf.setTextColor(...colors.textMuted);
        docPdf.text("No payments recorded yet for this project.", marginX, cursorY + 4);
        cursorY += 24;
      } else {
        autoTable(docPdf, {
          ...baseTableOptions(),
          startY: cursorY,
          head: [["Invoice Number", "Payment Date", "Amount", "Running Total", "Balance Remaining", "Notes"]],
          body: historyRows,
          columnStyles: {
            0: { cellWidth: 110 },
            1: { cellWidth: 75 },
            2: { cellWidth: 90, halign: "right" },
            3: { cellWidth: 90, halign: "right" },
            4: { cellWidth: 100, halign: "right" },
            5: { cellWidth: "auto" },
          },
          margin: { left: marginX, right: marginX, top: PDF_HEADER_BAND },
        });
        cursorY = docPdf.lastAutoTable.finalY + 26;
      }

      // ---- Repeated slim header (pages 2+) and footer (every page) ----
      const pageCount = docPdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i += 1) {
        docPdf.setPage(i);
        const pageHeight = docPdf.internal.pageSize.getHeight();

        if (i > 1) {
          docPdf.setFont(PDF_THEME.fontFamily, "bold");
          docPdf.setFontSize(11);
          docPdf.setTextColor(...colors.primaryDark);
          docPdf.text(COMPANY_NAME, marginX, 30);

          docPdf.setFont(PDF_THEME.fontFamily, "normal");
          docPdf.setFontSize(9);
          docPdf.setTextColor(...colors.textMuted);
          docPdf.text(
            `Payment History Report — ${managingInvoice.clientName || "Client"}`,
            pageWidth - marginX,
            30,
            { align: "right" },
          );

          docPdf.setDrawColor(...colors.accentGold);
          docPdf.setLineWidth(1);
          docPdf.line(marginX, 44, pageWidth - marginX, 44);
        }

        docPdf.setFont(PDF_THEME.fontFamily, "italic");
        docPdf.setFontSize(8.5);
        docPdf.setTextColor(...colors.textMuted);
        docPdf.text("Thank you for choosing Sarrathi Constructions.", marginX, pageHeight - 30);
        docPdf.text("This report is system generated.", marginX, pageHeight - 18);
        docPdf.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, pageHeight - 18, {
          align: "right",
        });
      }

      docPdf.save(`${sanitizeFileName(managingInvoice.clientName)}_Payment_History.pdf`);
    } catch (err) {
      console.error("Failed to generate payment history PDF:", err);
      toast.error("Failed to generate payment history PDF.");
    } finally {
      setPaymentsPdfGenerating(false);
    }
  };

  return (
    <div className="page-container invoice-page">
      <ToastContainer toasts={toasts} onDismiss={toast.dismiss} />

      <div className="page-header">
        <div>
          <h1>Payments</h1>
          <p>Track client payment history, timelines, and quotations.</p>
        </div>

        <div className="invoice-summary-cards">
          <div className="invoice-summary-card">
            <span className="invoice-summary-label">Total Collected</span>
            <span className="invoice-summary-value">{formatRupees(totalCollected)}</span>
          </div>
          <div className="invoice-summary-card outstanding">
            <span className="invoice-summary-label">Outstanding Balance</span>
            <span className="invoice-summary-value">{formatRupees(totalOutstanding)}</span>
          </div>
        </div>
      </div>

      <div className="invoice-toolbar">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search by invoice no., name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="invoice-table-card">
        <div className="invoice-table-scroll">
          <AnimatePresence mode="wait">
            <motion.table
              key={loading ? "loading" : "loaded"}
              className="invoice-table"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <thead>
                <tr>
                  <th>Invoice Number</th>
                  <th>Client Name</th>
                  <th>Project Type</th>
                  <th>Final Quotation</th>
                  <th>Amount Paid</th>
                  <th>Balance</th>
                  <th>Status</th>
                  <th>Payments</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="empty-row">
                      Loading payments...
                    </td>
                  </tr>
                ) : filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="empty-row">
                      No Projects Found
                    </td>
                  </tr>
                ) : (
                  paginatedInvoices.map((inv) => {
                    const status = statusFor(inv);
                    return (
                      <tr key={inv.id}>
                        <td>
                          <span className="invoice-number-badge main-invoice-badge">
                            {formatMainInvoiceNumber(inv)}
                          </span>
                        </td>
                        <td>
                          {inv.clientName || "-"}
                          {getProjectRef(clientsById, inv.clientDocId) && (
                            <span style={PROJECT_REF_BADGE_STYLE}>
                              {getProjectRef(clientsById, inv.clientDocId)}
                            </span>
                          )}
                        </td>
                        <td>{inv.projectType || "-"}</td>
                        <td className="amount-cell">{formatRupees(inv.finalEstimation)}</td>
                        <td className="amount-cell paid-cell">{formatRupees(inv.amountPaid)}</td>
                        <td className="amount-cell balance-cell">{formatRupees(inv.balanceAmount)}</td>
                        <td>
                          <span className={`status-pill ${statusPillClass(status)}`}>{status}</span>
                        </td>
                        <td>
                          <div className="action-icons">
                            <button
                              className="icon-btn add-payment-icon"
                              title={isPayable(inv) ? "Make Payment" : "Fully Paid"}
                              disabled={!isPayable(inv)}
                              onClick={() => openAddPayment(inv)}
                            >
                              <FaCoins />
                            </button>
                            <button
                              className="icon-btn payment-history-icon"
                              title="Manage Payments"
                              onClick={() => openManagePayments(inv)}
                            >
                              <FaWallet />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </motion.table>
          </AnimatePresence>
        </div>
      </div>
      <Pagination {...invoicesPagination} />

      {/* Manage Payments Modal */}
      <AnimatePresence>
        {showManageModal && managingInvoice && (
          <motion.div
            className="invoice-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="invoice-page-modal allocation-modal payment-history-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <h2 style={{ margin: 0 }}>
                  Manage Payments — {managingInvoice.clientName || "Client"}
                  {getProjectRef(clientsById, managingInvoice.clientDocId) && (
                    <span style={PROJECT_REF_BADGE_STYLE}>
                      {getProjectRef(clientsById, managingInvoice.clientDocId)}
                    </span>
                  )}
                </h2>
                <button
                  className="generate-pdf-btn"
                  onClick={generatePaymentsPDF}
                  disabled={paymentsPdfGenerating}
                  title="Download a Payment History Report for this client"
                >
                  <FaFilePdf />
                  {paymentsPdfGenerating ? "Generating..." : "Generate PDF"}
                </button>
              </div>

              <div className="view-grid move-invoice-summary">
                <div className="view-field">
                  <span className="view-label">Client ID</span>
                  <span className="view-value">
                    {managingInvoice.clientId || "-"}
                    {getProjectRef(clientsById, managingInvoice.clientDocId) && (
                      <span style={PROJECT_REF_BADGE_STYLE}>
                        {getProjectRef(clientsById, managingInvoice.clientDocId)}
                      </span>
                    )}
                  </span>
                </div>
                <div className="view-field">
                  <span className="view-label">Main Invoice Number</span>
                  <span className="view-value">{formatMainInvoiceNumber(managingInvoice)}</span>
                </div>
              </div>

              <div className="allocation-summary">
                <div className="allocation-chip quotation-chip">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <span className="view-label">Final Quotation</span>
                    {!editingQuotation && (
                      <button
                        type="button"
                        className="icon-btn edit-icon"
                        aria-label="Edit Final Quotation"
                        title="Edit Final Quotation"
                        onClick={openEditQuotation}
                      >
                        <FaEdit />
                      </button>
                    )}
                  </div>

                  {editingQuotation ? (
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        alignItems: "center",
                        marginTop: 8,
                      }}
                    >
                      <input
                        type="text"
                        inputMode="decimal"
                        className="payment-history-inline-input"
                        value={quotationInput}
                        onChange={handleQuotationInputChange}
                        autoFocus
                      />
                      <button
                        type="button"
                        className="icon-btn edit-icon"
                        aria-label="Save"
                        title="Save"
                        disabled={quotationSaving}
                        onClick={saveQuotation}
                      >
                        <FaCheck />
                      </button>
                      <button
                        type="button"
                        className="icon-btn delete-icon"
                        aria-label="Cancel"
                        title="Cancel"
                        disabled={quotationSaving}
                        onClick={cancelEditQuotation}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <span className="balance-amount-value small" style={{ marginTop: 4 }}>
                      {formatRupees(managingInvoice.finalEstimation)}
                    </span>
                  )}
                </div>
                <div className="allocation-chip">
                  <span className="view-label">Amount Paid</span>
                  <span className="balance-amount-value small">
                    {formatRupees(managingInvoice.amountPaid)}
                  </span>
                </div>
                <div className="allocation-chip">
                  <span className="view-label">Balance</span>
                  <span className="balance-amount-value small">
                    {formatRupees(managingInvoice.balanceAmount)}
                  </span>
                </div>
              </div>

              {quotationError && (
                <span className="field-error materials-form-error">{quotationError}</span>
              )}

              <h3 style={{ marginTop: 20, marginBottom: 8 }}>
                <FaHistory style={{ marginRight: 6 }} /> Payment History
              </h3>

              {managingPaymentsWithRunning.length === 0 ? (
                <div className="mb-empty">No payments recorded yet for this project.</div>
              ) : (
                <div className="payment-cards-grid">
                  {managingPaymentsWithRunning.map(({ entry, runningTotal, correctedBalance }) => {
                    const seq = getEntrySequence(entry);
                    const isFinal = correctedBalance <= 0;
                    return (
                      <div className="payment-card" key={seq}>
                        <div className="payment-card-top">
                          <span className="invoice-number-badge payment-card-number">
                            {formatInvoiceNumberDisplay(entry, managingInvoice)}
                          </span>
                          <span className={`payment-status-pill${isFinal ? " is-final" : ""}`}>
                            {isFinal ? "Fully Paid" : "Paid"}
                          </span>
                        </div>

                        <div className="payment-card-grid">
                          <div className="payment-card-field">
                            <span className="view-label">Payment Date</span>
                            <span className="view-value">{formatDate(entry.paymentDate)}</span>
                          </div>
                          <div className="payment-card-field">
                            <span className="view-label">Payment Amount</span>
                            <span className="view-value payment-card-amount">
                              {formatRupees(entry.paymentAmount)}
                            </span>
                          </div>
                          <div className="payment-card-field">
                            <span className="view-label">Running Total Paid</span>
                            <span className="view-value">{formatRupees(runningTotal)}</span>
                          </div>
                          <div className="payment-card-field">
                            <span className="view-label">Balance Remaining</span>
                            <span className="view-value">{formatRupees(correctedBalance)}</span>
                          </div>
                          <div className="payment-card-field payment-card-field--wide">
                            <span className="view-label">Payment Notes</span>
                            <span className="view-value">{entry.paymentNotes || "-"}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <p className="allocation-hint" style={{ marginTop: 16 }}>
                To record a new payment, use "Make Payment" from the table — it will
                appear here automatically.
              </p>

              <div className="invoice-modal-actions">
                <button className="cancel-btn" onClick={closeManagePayments}>
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Make Payment Modal — separate action from Manage Payments. Same
          fields, validation, and Firestore transaction as Invoice.js's own
          Add Payment modal, writing to the exact same invoice document. */}
      <AnimatePresence>
        {showAddPaymentModal && payingInvoice && (
          <motion.div
            className="invoice-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="invoice-page-modal add-payment-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Make Payment — {payingInvoice.clientName || "Client"}</h2>
              <p className="allocation-hint">
                This will generate a new permanent payment invoice under{" "}
                <strong>{formatMainInvoiceNumber(payingInvoice)}</strong> and reduce the
                balance automatically. Earlier invoices are never changed.
              </p>

              <div className="view-grid move-invoice-summary">
                <div className="view-field">
                  <span className="view-label">Final Quotation</span>
                  <span className="view-value">
                    {formatRupees(payingInvoice.finalEstimation)}
                  </span>
                </div>
                <div className="view-field">
                  <span className="view-label">Current Balance</span>
                  <span className="view-value">
                    {formatRupees(payingInvoice.balanceAmount)}
                  </span>
                </div>
              </div>

              <div className="invoice-form-grid">
                <div className="form-field-wrap">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Payment Amount"
                    value={paymentAmount}
                    onChange={handlePaymentAmountChange}
                    autoFocus
                  />
                </div>

                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => {
                    setPaymentDate(e.target.value);
                    if (paymentError) setPaymentError("");
                  }}
                />
              </div>

              <div className="balance-display invoice-edit-balance">
                <span className="view-label">Balance After This Payment</span>
                <span className="balance-amount-value">
                  {formatRupees(paymentPreviewBalance)}
                </span>
              </div>

              {paymentError && (
                <span className="field-error materials-form-error">{paymentError}</span>
              )}

              <div className="invoice-modal-actions">
                <button className="cancel-btn" disabled={paymentSaving} onClick={closeAddPayment}>
                  Cancel
                </button>
                <button className="save-btn" disabled={paymentSaving} onClick={saveAddPayment}>
                  {paymentSaving ? "Saving..." : "Save Payment"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Payments;